import React, { useState, useEffect } from "react";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import styles from "./resetPasswordPage.module.scss";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  resetPassword,
  getUserDetailsByToken,
} from "../../Api/forgotpasswordAPI";
import { getAccessTokenAPI } from "Api/getAccessToken";
import { useGlobalContext } from "context/globalContext";
import { consoleLog } from "helpers";

export default function ResetPasswordPage() {
  const { id } = useParams();
  const { accessJWT, setAccessJWT } = useGlobalContext();

  const [userId, setUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState("");
  const navigate = useNavigate();
  consoleLog([resetPasswordToken]);
  useEffect(() => {
    setResetPasswordToken(id);
  }, [id]);

  useEffect(() => {
    async function getAccessToken() {
      let success = false;
      let count = 0;
      while (!success && count <= 4) {
        try {
          const response = await getAccessTokenAPI();
          if (response?.statusCode === 200 && response?.data) {
            setAccessJWT(response?.data);
            localStorage.setItem("jwtToken", response?.data);
            success = true;
          }
        } catch (err) {
          console.error("Error fetching access token:", err);
        }
        count++;
      }
    }

    getAccessToken();
  }, []);

  useEffect(() => {
    if (accessJWT && id) {
      getUserDetails();
    }

    async function getUserDetails() {
      const response = await getUserDetailsByToken(id, () => {}, accessJWT);
      if (response?.statusCode !== 200) {
        if (
          response?.statusCode === 400 &&
          response?.message === "User details not found."
        ) {
          setUserId("tokenExpired");
        }
        return;
      }
      setUserId(response?.data?.id);
    }
  }, [accessJWT, id]);

  const passwordValidationRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  async function handleUpdateClick() {
    if (userId === "tokenExpired") {
      toast.error("The reset link has expired.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (!passwordValidationRegex.test(newPassword)) {
      toast.error(
        "Password must contain at least 1 uppercase, 1 lowercase, 1 number, 1 special character, and be at least 8 characters long."
      );
      return;
    }

    const payload = {
      id: userId,
      newPassword: newPassword.trim(),
    };

    const response = await resetPassword(payload, () => {}, accessJWT);

    if (response?.statusCode !== 200) {
      toast.error("Something went wrong while updating the password.");
      return;
    }

    if (response.message === "Password updated successfully") {
      toast.success("Password updated. Redirecting...");
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } else {
      toast.error(response?.message || "Failed to update password");
    }
  }

  const requirements = [
    { id: "uppercase", label: "1 UPPERCASE letter" },
    { id: "lowercase", label: "1 lowercase letter" },
    { id: "number", label: "1 number" },
    { id: "special", label: "1 special character" },
    { id: "length", label: "At least 8 characters" },
  ];

  return (
    <React.Fragment>
      <div className={styles.mainContainer}>
        <div className={styles.inputLabelContainer}>
          <label className={styles.label}>NEW PASSWORD</label>
          <div>
            <TextField
              key={showNewPassword.toString()}
              inputMode={showNewPassword ? "text" : "none"}
              type={showNewPassword ? "text" : "password"}
              size="small"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
              }}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      edge="end"
                      aria-label={
                        showNewPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showNewPassword ? (
                        <Visibility sx={{ height: "3vh" }} />
                      ) : (
                        <VisibilityOff sx={{ height: "3vh" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                style: {
                  height: "30px",
                  width: "280px",
                  fontSize: "12px",
                  color: "#696969",
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: "12px",
                },
              }}
            />
          </div>
        </div>
        <div className={styles.inputLabelContainer}>
          <label className={styles.label}>CONFIRM PASSWORD</label>
          <div>
            <TextField
              key={showConfirmPassword.toString()}
              size="small"
              inputMode={showConfirmPassword ? "text" : "none"}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showConfirmPassword ? (
                        <Visibility sx={{ height: "3vh" }} />
                      ) : (
                        <VisibilityOff sx={{ height: "3vh" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                style: {
                  height: "30px",
                  width: "280px",
                  fontSize: "12px",
                  color: "#696969",
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: "12px",
                },
              }}
            />
          </div>
        </div>
        <div className={styles.requirementContainer}>
          <h1 className={styles.heading}>Minimum password requirements</h1>
          <ul className={styles.requirementsList}>
            {requirements.map((req) => (
              <li key={req.id}>{req.label}</li>
            ))}
          </ul>
        </div>

        <Button
          onClick={handleUpdateClick}
          size="small"
          className={styles.button}
          sx={{
            color: "secondary.contrastText",
            backgroundColor: "secondary.main",
            ":hover": { bgcolor: "secondary.light" },
          }}
        >
          Save changes
        </Button>
      </div>
    </React.Fragment>
  );
}
